/**
 * Inbox panel — tasks that aren't on the calendar yet.
 *
 * Two reasons a task ends up here:
 *   1. The user explicitly chose "Inbox only" at create time (or
 *      their pref for that work level is 'never').
 *   2. The scheduler couldn't find a slot — no working hours, or
 *      the next 30 days are too full.
 *
 * From the inbox you can:
 *   - Schedule one (runs the scheduler now → proposed/scheduled).
 *   - Mark done (e.g. "actually I already did this").
 *   - Delete.
 *
 * Lives inside the /calendar screen so the inbox is one click from
 * the place a user goes to think about their week.
 */

import { useEffect, useState } from 'react';
import { api, type PersonalTask } from '../api';
import { useToast } from './Toast';
import { IconClock, IconTrash } from './Icon';

export function TaskInbox({ onChanged }: { onChanged: () => void }) {
  const [items, setItems] = useState<PersonalTask[] | null>(null);
  const { toast } = useToast();

  async function load(): Promise<void> {
    const r = await api.listPersonalTasks('inbox');
    setItems(r.tasks);
  }

  useEffect(() => {
    void load();
    const onChange = () => void load();
    window.addEventListener('synfolio:tasks-changed', onChange);
    return () => window.removeEventListener('synfolio:tasks-changed', onChange);
  }, []);

  async function scheduleNow(t: PersonalTask): Promise<void> {
    const r = await api.reschedulePersonalTask(t.id);
    if (r.found) {
      toast({
        tone: 'success',
        title: 'Found a slot',
        body: 'Check the toast or your calendar.',
      });
      window.dispatchEvent(new Event('synfolio:proposal-changed'));
    } else {
      toast({
        tone: 'warning',
        title: 'Still no room',
        body: 'Widen your working hours or shrink the estimate.',
      });
    }
    await load();
    onChanged();
  }

  async function markDone(t: PersonalTask): Promise<void> {
    await api.patchPersonalTask(t.id, { status: 'done' });
    await load();
    onChanged();
  }

  async function remove(t: PersonalTask): Promise<void> {
    if (!confirm(`Delete "${t.title}"?`)) return;
    await api.deletePersonalTask(t.id);
    await load();
    onChanged();
  }

  if (!items || items.length === 0) return null;

  return (
    <section className="panel flex flex-col gap-2 p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">Inbox</h2>
        <span className="text-[10px] text-cream-3">
          {items.length} task{items.length === 1 ? '' : 's'} without a slot
        </span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {items.map((t) => (
          <li
            key={t.id}
            className="flex flex-wrap items-center gap-2 rounded-lg bg-ink-3 p-2.5"
          >
            <span
              aria-hidden
              className="h-2 w-2 flex-shrink-0 rounded-full"
              style={{
                background:
                  t.workLevel === 'deep'
                    ? 'var(--color-clove)'
                    : t.workLevel === 'shallow'
                      ? 'var(--color-leaf)'
                      : 'var(--color-mist)',
              }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{t.title}</div>
              <div className="flex items-center gap-1.5 text-[11px] text-cream-3">
                <span>{t.workLevel}</span>
                <span aria-hidden>·</span>
                <IconClock size={10} />
                <span>{t.estimatedMinutes} min</span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary !px-2 !py-1 text-[11px]"
              onClick={() => void scheduleNow(t)}
            >
              Schedule
            </button>
            <button
              type="button"
              className="btn btn-ghost !px-2 !py-1 text-[11px]"
              onClick={() => void markDone(t)}
            >
              Done
            </button>
            <button
              type="button"
              className="btn btn-ghost !px-2 !py-1 text-[11px] text-rust"
              onClick={() => void remove(t)}
              aria-label="Delete"
            >
              <IconTrash size={12} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
